<template>
  <div class="checklist-row" :class="{ checked, disabled }" @click="onRowClick">
    <Checkbox
      :id="id"
      :model-value="checked"
      :disabled="disabled"
      :aria-labelledby="`${id}-title`"
      :aria-describedby="subtitle ? `${id}-subtitle` : undefined"
      @update:model-value="emit('toggle', Boolean($event))"
    />
    <!-- A photo or an avatar before the name -->
    <slot name="leading" />
    <View direction="column" gap="xs" class="identity">
      <View gap="sm" align="center" wrap="wrap">
        <Text :id="`${id}-title`" as="span" variant="label-medium" :color="disabled ? 'tertiary' : 'primary'">
          {{ title }}
        </Text>
        <!-- A badge that belongs beside the name ("Live on Zillow") -->
        <slot name="title-suffix" />
      </View>
      <Text
        v-if="subtitle"
        :id="`${id}-subtitle`"
        as="span"
        variant="body-small"
        :color="disabled ? 'tertiary' : 'secondary'"
      >
        {{ subtitle }}
      </Text>
    </View>
    <!-- A control of its own (a status, a field); a click on it is not a toggle -->
    <div v-if="$slots.trailing" class="trailing" @click.stop>
      <slot name="trailing" />
    </div>
  </div>
</template>

<script setup lang="ts">
import Checkbox from './checkbox.vue';
import Text from './text.vue';
import View from './view/view.vue';

/**
 * One choice of a checklist, as a card: the checkbox, what it is, and anything that goes with it.
 * A disabled row stays in the list, greyed, with the reason as its subtitle.
 */
const props = defineProps<{ id: string; title: string; subtitle?: string; checked: boolean; disabled?: boolean }>();
const emit = defineEmits<{ toggle: [checked: boolean] }>();

/** A click anywhere on the row but the checkbox itself, which toggles on its own */
function onRowClick(event: MouseEvent) {
  if (props.disabled || (event.target as HTMLElement).closest('.checkbox')) {
    return;
  }

  emit('toggle', !props.checked);
}
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.checklist-row {
  display: flex;
  align-items: center;
  gap: calc(var(--t-spacing-sm) + var(--t-spacing-xs));
  padding: calc(var(--t-spacing-sm) + var(--t-spacing-xs));

  border: 1px solid var(--t-color-border-secondary);
  border-radius: var(--t-border-radius-md);
  background-color: var(--t-color-neutral-white);
  transition: border-color 150ms var(--t-ease-in-out);
  cursor: pointer;

  &.checked {
    border-color: var(--t-color-neutral-950);
  }

  &:hover:not(.disabled) {
    border-color: var(--t-color-neutral-700);
  }

  // The checkbox's focus is the row's; a field in the trailing slot draws its own ring
  &:has(> .checkbox input:focus-visible) {
    outline: 2px solid var(--t-color-neutral-950);
    outline-offset: 2px;
  }

  // Greyed, not gone: the reason it cannot be picked stays readable
  &.disabled {
    opacity: 0.75;
    background-color: var(--t-color-neutral-50);
    cursor: default;
  }

  // A phone has no room beside the name: the trailing controls take a line of their own
  @include mobile {
    flex-wrap: wrap;
  }
}

// A long name or address has nowhere to break, so it wraps rather than pushing the row past a phone's width
.identity {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}

.trailing {
  display: flex;
  align-items: center;
  gap: var(--t-spacing-sm);
  flex-shrink: 0;

  @include mobile {
    flex-basis: 100%;
    // Under the name, past the checkbox
    padding-left: calc(var(--t-spacing-lg) + var(--t-spacing-sm));
  }
}
</style>
